import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'react-bootstrap';
import api from '@/shared/services/api';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { resolveRoles } from '@/shared/utils/previewRoles';

const AUDIENCE_LABELS = {
    faculty: 'faculty',
    student: 'students',
    coordinator: 'LA coordinators',
};

const GUIDE_ERROR = 'The guide could not be opened. Try again in a moment.';

/**
 * Whether the signed-in viewer is shown the guide for an audience. The server
 * decides who may fetch the coordinator guide; this decides who is shown the
 * link, so the rules here match its audiences and the flags of the workflows
 * each guide describes.
 *
 * The faculty guide is hidden from an account that holds only the student
 * role, and while a signed-in account's roles are still loading. The student
 * guide follows studentApplication and the coordinator guide follows
 * coordinatorReview.
 */
export function useGuideVisible(audience) {
    const { currentUser, userProfile, effectiveRoles } = useAuth();
    const studentApplicationEnabled = useFeature('studentApplication');
    const coordinatorReviewEnabled = useFeature('coordinatorReview');
    const roles = resolveRoles({ effectiveRoles, userProfile });

    if (audience === 'student') return studentApplicationEnabled;
    if (audience === 'coordinator') {
        return (
            coordinatorReviewEnabled &&
            roles.some((role) => ['la_coordinator', 'admin'].includes(role))
        );
    }
    if (currentUser && !userProfile) return false;
    return !(roles.length > 0 && roles.every((role) => role === 'student'));
}

async function openCoordinatorGuide() {
    // Opened before the request so the click that started it still counts as
    // a user gesture; a blocked pop-up falls back to a download below.
    const tab = window.open('', '_blank');
    try {
        const response = await api.get('/guides/coordinator', {
            responseType: 'blob',
        });
        const url = URL.createObjectURL(response.data);
        if (tab) {
            tab.opener = null;
            tab.location.href = url;
        } else {
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = 'PSH_Coordinator_Step_by_Step_Guide.pdf';
            anchor.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
        if (tab) tab.close();
        throw error;
    }
}

/**
 * A link to one step-by-step guide (PDF). Renders nothing when the viewer is
 * not meant to see that guide. The faculty and student guides are plain links
 * to the public route; the coordinator guide needs the viewer's token, so it
 * is fetched with it and opened from the response.
 */
function GuideLink({ audience, className = '', short = false }) {
    const visible = useGuideVisible(audience);
    const [failed, setFailed] = useState(false);

    if (!visible) return null;

    // The short form sits in a page header. Its visible words are the start of
    // the accessible name, which carries the audience (WCAG 2.5.3).
    const text = short
        ? 'Guide (PDF)'
        : `Step-by-step guide for ${AUDIENCE_LABELS[audience]} (PDF)`;
    const audienceHint = short ? ` for ${AUDIENCE_LABELS[audience]}` : '';

    if (audience !== 'coordinator') {
        return (
            <a
                href={`${api.defaults.baseURL}/guides/${audience}`}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
            >
                {text}
                <span className="visually-hidden">
                    {audienceHint} (opens in a new tab)
                </span>
            </a>
        );
    }

    const handleClick = async () => {
        setFailed(false);
        try {
            await openCoordinatorGuide();
        } catch {
            setFailed(true);
        }
    };

    return (
        <span className={className}>
            <Button
                variant="link"
                className="p-0 border-0 align-baseline"
                onClick={handleClick}
            >
                {text}
                <span className="visually-hidden">
                    {audienceHint} (opens in a new tab)
                </span>
            </Button>
            {failed && (
                <span role="alert" className="d-block text-danger small">
                    {GUIDE_ERROR}
                </span>
            )}
        </span>
    );
}

GuideLink.propTypes = {
    audience: PropTypes.oneOf(['faculty', 'student', 'coordinator']).isRequired,
    className: PropTypes.string,
    short: PropTypes.bool,
};

/**
 * A guide link in a card, for the dashboards: one sentence on what the guide
 * covers above the link. Renders nothing when the link would not show.
 */
export function GuideCallout({ audience, children, className = 'mb-3' }) {
    const visible = useGuideVisible(audience);
    if (!visible) return null;

    return (
        <Card className={className}>
            <Card.Body className="py-3">
                <p className="mb-1">{children}</p>
                <GuideLink audience={audience} />
            </Card.Body>
        </Card>
    );
}

GuideCallout.propTypes = {
    audience: PropTypes.oneOf(['faculty', 'student', 'coordinator']).isRequired,
    children: PropTypes.node,
    className: PropTypes.string,
};

export default GuideLink;
